import Permissions from '@app/src/auth/permissions'
import { usePermissions } from '@app/src/auth/permissions/usePermissions'
import EmptyState from '@app/src/components/EmptyState'
import Avatar from '@app/src/components/Ui/Avatar'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import AutomationCard from '@app/src/pages/Configurations/ConfigurationsPages/Automation/AutomationCard'
import type { AutomationCardItem } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/AutomationsConfigLogic'
import { canShowAutoAcceptProductMappingResponses } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/AutomationsConfigLogic'
import CreateAutomationModal from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import CreateExpectationAutomationModal from '@app/src/pages/Configurations/ConfigurationsPages/Automation/ExpectationAutomation/CreateExpectationAutomationModal'
import { useAutomations } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Hooks/useAutomations'
import { Sync, SyncDisabled, TaskOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useConfirm } from 'material-ui-confirm'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'

const AutomationsConfig: React.FC = () => {
  const { productMapping } = useFlags()
  const { formatMessage } = useIntl()
  const [isAutomationDialogOpen, openAutomationDialog, closeAutomationDialog] = useDialogState(false)
  const [isExpectationAutomationDialogOpen, openExpectationAutomationDialog, closeExpectationAutomationDialog] =
    useDialogState(false)
  const { settings, updateOrganizationSettings, isLoading, isSaving, isFetching } = useOrganizationSettings()
  const confirm = useConfirm()
  const { hasPermission } = usePermissions()
  const showAutoAcceptProductMappingResponses = canShowAutoAcceptProductMappingResponses({
    isProductMappingEnabled: Boolean(productMapping),
    hasFullProductMappingAccess: hasPermission(Permissions.FULL_DASHBOARD_ACCESS),
    isSourcingUser: hasPermission(Permissions.SOURCING_USER),
    isTransparencyUser: hasPermission(Permissions.TRANSPARENCY_USER),
  })
  const [editedAutomation, setEditedAutomation] = useState<AutomationCardItem>()
  const location = useLocation<{ openCreateDialog: boolean }>()

  const { automations, refetchAll } = useAutomations()

  const editAutomation = (automation: AutomationCardItem) => {
    setEditedAutomation(automation)
    if (automation.type === 'expectation') openExpectationAutomationDialog()
    else openAutomationDialog()
  }

  const handleSelectExpectation = () => {
    closeAutomationDialog()
    setEditedAutomation(undefined)
    openExpectationAutomationDialog()
  }

  const handleCreateNewAutomation = (): void => {
    setEditedAutomation(undefined)
    openAutomationDialog()
  }

  useEffect(() => {
    if (!isAutomationDialogOpen) {
      setEditedAutomation(undefined)
    }
  }, [isAutomationDialogOpen])

  useEffect(() => {
    if (location.state?.openCreateDialog) {
      openAutomationDialog()
    }
  }, [])

  const handleAutoAcceptProductMappingResponseSwitch = (checked: boolean) => {
    if (checked) {
      confirm({
        content: formatMessage({ id: 'automations.productMapping.acceptLatestValueChains.confirmationDialog.text' }),
        confirmationText: formatMessage({
          id: 'automations.productMapping.acceptLatestValueChains.confirmationDialog.button',
        }),
      }).then(() => updateOrganizationSettings({ autoAcceptProductMappingResponses: true }))
      return
    }
    updateOrganizationSettings({ autoAcceptProductMappingResponses: false })
  }

  return (
    <>
      <Box display="flex" flexDirection="column" width="100%" height="100%">
        <ConfigurationCollection
          actionButtons={[
            {
              label: formatMessage({ id: 'automatedSendout.create' }),
              variant: 'contained',
              onClick: handleCreateNewAutomation,
            },
          ]}
          enablePadding={{ right: true }}
        >
          {showAutoAcceptProductMappingResponses && (
            <Box>
              <Typography variant="h6" mb={2}>
                {formatMessage({ id: 'navbar.productMapping' })}
              </Typography>
              <Paper
                sx={({ palette }) => ({
                  boxShadow: 'none',
                  border: `1px solid ${palette.grey[300]}`,
                  mb: 4,
                })}
              >
                <List>
                  <ListItem
                    secondaryAction={
                      <FormControlLabel
                        disabled={isLoading || isSaving || isFetching}
                        control={
                          <Switch
                            checked={Boolean(settings?.autoAcceptProductMappingResponses)}
                            onChange={(_, checked) => handleAutoAcceptProductMappingResponseSwitch(checked)}
                          />
                        }
                        label={
                          isLoading || isSaving || isFetching ? (
                            <Skeleton width={20} variant="rounded" />
                          ) : settings?.autoAcceptProductMappingResponses ? (
                            formatMessage({ id: 'automations.on' })
                          ) : (
                            formatMessage({ id: 'automations.off' })
                          )
                        }
                      />
                    }
                  >
                    <ListItemAvatar>
                      {settings?.autoAcceptProductMappingResponses ? (
                        <Avatar sx={{ backgroundColor: 'info.dark', color: 'text.primary' }}>
                          <Sync />
                        </Avatar>
                      ) : (
                        <Avatar sx={{ backgroundColor: 'info.light', color: 'text.disabled' }}>
                          <SyncDisabled />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText disableTypography>
                      <Typography variant="subtitle1">
                        {formatMessage({ id: 'automations.productMapping.acceptLatestValueChains.title' })}
                      </Typography>
                      <Typography variant="body1">
                        {formatMessage({ id: 'automations.productMapping.acceptLatestValueChains.description' })}
                      </Typography>
                    </ListItemText>
                  </ListItem>
                </List>
              </Paper>
            </Box>
          )}
          <Grid container spacing={2}>
            {automations.length ? (
              automations.map(automation => (
                <Grid item xs={12} lg={6} xl={4} key={`${automation.type}-${automation.id}`}>
                  <AutomationCard
                    automation={automation}
                    editAutomation={() => editAutomation(automation)}
                    onDeleted={refetchAll}
                  />
                </Grid>
              ))
            ) : (
              <EmptyState
                iconComponent={TaskOutlined}
                title={formatMessage({ id: 'automatedSendout.emptyState.title' })}
                description={formatMessage({ id: 'automatedSendout.emptyState.description' })}
              >
                <Button variant="outlined" onClick={handleCreateNewAutomation}>
                  {formatMessage({ id: 'automatedSendout.emptyState.actionButtonText' })}
                </Button>
              </EmptyState>
            )}
          </Grid>
        </ConfigurationCollection>
      </Box>
      <CreationModalProgressContextProvider>
        <CreateAutomationModal
          onClose={closeAutomationDialog}
          open={isAutomationDialogOpen}
          editedAutomation={editedAutomation}
          onSaved={refetchAll}
          onSelectExpectation={handleSelectExpectation}
        />
      </CreationModalProgressContextProvider>
      <CreationModalProgressContextProvider>
        <CreateExpectationAutomationModal
          open={isExpectationAutomationDialogOpen}
          onClose={closeExpectationAutomationDialog}
          editedExpectationAutomation={editedAutomation?.expectationAutomation}
          onSaved={refetchAll}
        />
      </CreationModalProgressContextProvider>
    </>
  )
}
export default AutomationsConfig
